import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DepartmentService, IBaseDepartment } from '@shared/services/department.service';
import { EmployeeService, IMesUser } from '@shared/services/mesuser.service';
import { ITeamProfile, TeamProfileService } from '@shared/services/teamProfiles.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-employee-dialog',
	templateUrl: './create-update-employee.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateEmployeeDialogComponent implements OnInit {
	@Output() saved: EventEmitter<any> = new EventEmitter();
	form: FormGroup;
	employee: IMesUser;
	teamProfiles: ITeamProfile[] = [];
	departments: IBaseDepartment[] = [];
	activeDepartments: IBaseDepartment[] = [];
	selectedDepartment: string | null = null;
	namePattern: string = '^[0-9a-zA-ZÆØÅæøå\\-_#\\s]*$';
	httpError: string = '';
	loading: boolean = false;
	isEdit: boolean = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public editEmployee: IMesUser,
		public dialogRef: MatDialogRef<CreateUpdateEmployeeDialogComponent>,
		public employeeService: EmployeeService,
		public teamProfileService: TeamProfileService,
		public departmentService: DepartmentService,
		public dialog: MatDialog,
		private fb: FormBuilder,
	) {
		if (editEmployee == null) {
			this.employee = {
				id: null,
				alias: '',
				firstName: '',
				lastName: '',
				isActive: true,
				salaryCode: '',
				teamProfileId: '',
				departmentId: '',
				firstWorkDate: null,
			};
		} else {
			this.isEdit = true;
			this.employee = { ...editEmployee };
		}
	}

	ngOnInit(): void {
		this.teamProfileService.getTeamProfiles().subscribe((c) => {
			this.teamProfiles = c.result;
			this.sortTeamProfiles();
		});

		this.departmentService.getDepartments().subscribe((department) => {
			this.departments = department.result;
			this.setDepartmentsToShow();
		});

		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.employee.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias), Validators.pattern(ValidatorPatterns.NoStartOrEndWhitespacePattern)]],
			firstName: [this.employee.firstName, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			lastName: [this.employee.lastName, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			departmentId: [this.employee.departmentId, [Validators.required]],
			teamProfileId: [this.employee.teamProfileId, [Validators.required]],
			firstWorkDate: [this.employee.firstWorkDate ? new Date(this.employee.firstWorkDate) : null],
			isActive: [this.employee.isActive],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.employee) {
					this.employee[key] = value[key];
				}
			});
		});
	}

	// Set which departments to show based on whether we're editing or creating
	setDepartmentsToShow() {
		if (this.employee && this.employee.departmentId) {
			const employeeDepartment = this.departments.find((dept) => dept.id === this.employee.departmentId);
			if (employeeDepartment && !employeeDepartment.isActive) {
				this.activeDepartments = [...this.departments.filter((dept) => dept.isActive), employeeDepartment];
			} else {
				this.activeDepartments = this.departments.filter((dept) => dept.isActive);
			}

			this.sortActiveDepartments();
			this.selectedDepartment = this.employee.departmentId;
		} else {
			this.activeDepartments = this.departments.filter((dept) => dept.isActive);
			this.sortActiveDepartments();
		}
	}

	sortTeamProfiles() {
		this.teamProfiles.sort((a, b) => a.alias.localeCompare(b.alias));
	}

	sortActiveDepartments() {
		this.activeDepartments.sort((a, b) => a.alias.localeCompare(b.alias));
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	get controls() {
		return this.form.controls;
	}

	onCancel = () => this.dialogRef.close();

	async onSave() {
		this.httpError = '';
		if (this.form.valid) {
			if (this.loading) return;
			this.loading = true;
			this.employee.salaryCode = this.employee.alias;

			// Convert firstWorkDate from Date to ISO date string (yyyy-MM-dd)
			const dateValue = this.form.get('firstWorkDate').value;
			this.employee.firstWorkDate = dateValue instanceof Date ? `${dateValue.getFullYear()}-${(dateValue.getMonth() + 1).toString().padStart(2, '0')}-${dateValue.getDate().toString().padStart(2, '0')}` : dateValue || null;

			if (this.employee.id == null) {
				this.employeeService.createEmployee(this.employee).subscribe({
					next: () => {
						this.saved.emit();
						this.dialogRef.close();
					},
					error: (errorResponse) => {
						this.httpError = `${errorResponse.status}`;
						this.loading = false;
					},
				});
			} else {
				this.employeeService.updateEmployee(this.employee).subscribe({
					next: () => {
						this.saved.emit();
						this.dialogRef.close();
					},
					error: (errorResponse) => {
						this.httpError = `${errorResponse.status}`;
						this.loading = false;
					},
				});
			}
		}
	}
}
